import {
  createClient,
  REALTIME_SUBSCRIBE_STATES,
  type SupabaseClient,
  type RealtimeChannel,
} from '@supabase/supabase-js';
import { auth } from '../Firebase/firebase';
import * as Sentry from '@sentry/react-native';
import { captureOperationalEvent } from '../analytics/operationalTelemetry';
import { type Database } from '@atlas/supabase-types';

// Typed Supabase client
export type TypedSupabaseClient = SupabaseClient<Database>;

// Get Supabase configuration from environment variables
const SUPABASE_URL = process.env.EXPO_PUBLIC_SUPABASE_URL;
const SUPABASE_KEY = process.env.EXPO_PUBLIC_SUPABASE_KEY;

if (!SUPABASE_URL || !SUPABASE_KEY) {
  console.warn('⚠️  Supabase configuration missing. Please set EXPO_PUBLIC_SUPABASE_URL and EXPO_PUBLIC_SUPABASE_KEY');
}

// Create Supabase client singleton
let supabaseClient: TypedSupabaseClient | null = null;

/**
 * Get or create typed Supabase client instance
 * @returns Supabase client instance or null if not configured
 */
export const getSupabaseClient = (): TypedSupabaseClient | null => {
  if (!SUPABASE_URL || !SUPABASE_KEY) {
    return null;
  }

  if (!supabaseClient) {
    supabaseClient = createClient<Database>(SUPABASE_URL, SUPABASE_KEY, {
      auth: {
        autoRefreshToken: false, // We'll handle token refresh manually with Firebase
        persistSession: false, // We use Firebase Auth, not Supabase Auth
      },
      realtime: {
        params: {
          eventsPerSecond: 10,
        },
      },
    });
  }

  return supabaseClient;
};

/**
 * Set authentication token for Supabase client
 * In Phase 1, we use Firebase Auth tokens
 * @param token - Firebase ID token
 */
export const setSupabaseAuthToken = async (_token: string): Promise<void> => {
  const client = getSupabaseClient();
  if (!client) {
    return;
  }

  try {
    // Set the auth token in the client's headers
    // Note: Supabase client doesn't directly support custom tokens in this way
    // We'll need to use the API client with Bearer token instead
    // This function is kept for future use when we migrate to Supabase Auth
    console.log('Setting Supabase auth token');
  } catch (error) {
    console.error('Error setting Supabase auth token:', error);
    Sentry.captureException(error, {
      tags: {
        supabase_auth_error: true,
      },
    });
  }
};

/**
 * Get current Firebase auth token and set it for Supabase
 * This is used in Phase 1 when we still use Firebase Auth
 */
export const refreshSupabaseAuth = async (): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      await setSupabaseAuthToken(token);
    }
  } catch (error) {
    console.error('Error refreshing Supabase auth:', error);
    Sentry.captureException(error, {
      tags: {
        supabase_auth_refresh_error: true,
      },
    });
  }
};

import { type TableName } from '@atlas/supabase-types';

// Re-export for convenience
export type { TableName };

// Type for realtime payload
export interface RealtimePayload<T extends TableName> {
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
  new: Database['public']['Tables'][T]['Row'];
  old: Database['public']['Tables'][T]['Row'];
}

// ---------------------------------------------------------------------------
// Realtime channel health tracking
// ---------------------------------------------------------------------------
//
// ATL-961 — the realtime CHANNEL_ERROR fan-out: one dead socket reported as
// ~25 separate crashes.
//
// WHY THIS EXISTS
//
// `.subscribe()` used to call `Sentry.captureException` unconditionally on
// every `CHANNEL_ERROR`. That is wrong by a factor of ~25, because
// `CHANNEL_ERROR` is not a per-channel signal: realtime-js's
// `RealtimeClient._onConnClose` / `_onConnError` both call
// `_triggerChanError()`, which is
// `this.channels.forEach((c) => c._trigger(CHANNEL_EVENTS.error))`. One dead
// WebSocket therefore fires the status callback once per open channel, and
// `setupAllSubscriptions()` opens ~25 of them. The client also force-closes
// its own socket after a single missed 25s heartbeat and then retries forever
// on a [1s, 2s, 5s, 10s] ladder, each failed attempt re-firing the whole
// fan-out. A till in a tunnel produced hundreds of identical events for one
// incident (Sentry issue 7669798073: 100 sampled events = 6 real incidents,
// bursts of 4/25/6/16/24/25 distinct channel names inside a single second).
//
// The fix is NOT to stop reporting. These channels only exist for sessions
// reading from Supabase, so this is the only visibility we have into the
// freshness mechanism the read cutover depends on, and there is a real failure
// it must not hide: ATL-830 made the confidentiality boundary a hand-maintained
// per-table `anon` column grant, so a table subscribed without its grant fails
// `subscription_check_filters` on every rejoin, forever, and the till silently
// shows stale prices and stock.
//
// So we report the OUTAGE rather than the fan-out, once, after the transient
// window has passed, and we say which kind it is:
//
//   * socket disconnected  -> the device lost the network. For a POS on mobile
//     data this is a normal operating condition, not a crash (the policy
//     already written down in utils/sentryFilters.js, ATL-563). Reported at
//     `warning`, and only if it outlasts the reconnect ladder.
//   * socket connected but channels still failing -> the channels themselves
//     are being rejected (missing publication entry, missing anon column
//     grant, bad filter). Realtime is structurally dead for this org.
//     Reported at `error`, with the server's own reason string, which the old
//     code threw away.

/** A channel's subscription health for the current app session. */
interface ChannelHealth {
  /** True once the channel has reached SUBSCRIBED at least once this session. */
  everSubscribed: boolean;
  /** Timestamp of the first error since the last SUBSCRIBED; null while healthy. */
  downSince: number | null;
  /** Server-supplied reason from the most recent failure, if any. */
  lastError: string | null;
  /**
   * True when the most recent failure was a join TIMEOUT rather than an error.
   *
   * ATL-1405: `channel_rejected` means "socket connected, channel not joined", and 121 of the 136
   * such outages in the week to 2026-09-23 carried NO server reason — i.e. the join never got an
   * answer at all, which is a slow/lossy link, not a server refusal. Counting them separately keeps
   * the two from being read as one failure.
   */
  lastFailureTimedOut: boolean;
}

/**
 * Delays between successive reports for one continuous outage.
 *
 * The first is deliberately longer than anything the transport can recover
 * from on its own: 60s is >2x realtime-js's 25s heartbeat interval and well
 * past its [1s, 2s, 5s, 10s] reconnect ladder, so an ordinary blip and several
 * consecutive failed retries all resolve below it and emit nothing at all.
 * The ladder then escalates and STOPS, so a device that spends an hour
 * underground costs 3 events, not thousands.
 */
export const REALTIME_OUTAGE_REPORT_DELAYS_MS = [60_000, 300_000, 1_800_000];

const realtimeChannelHealth = new Map<string, ChannelHealth>();
let outageTimer: ReturnType<typeof setTimeout> | null = null;
let outageReportIndex = 0;
/**
 * Channel names already described by a report in the CURRENT outage episode.
 *
 * The ladder above stops after 3 reports, and the episode only ends when EVERY channel recovers.
 * Without this set, one channel that can never subscribe pins the episode open forever: the ladder
 * exhausts ~36 minutes in and realtime then goes unreported for the rest of the session, including
 * a later, genuinely different outage. Re-arming on a channel nobody has reported yet keeps the
 * bound (a re-armed ladder still waits 60s before it says anything) while restoring the signal.
 */
let reportedDownChannels = new Set<string>();

const getChannelHealth = (channelName: string): ChannelHealth => {
  let health = realtimeChannelHealth.get(channelName);
  if (!health) {
    health = { everSubscribed: false, downSince: null, lastError: null, lastFailureTimedOut: false };
    realtimeChannelHealth.set(channelName, health);
  }
  return health;
};

const getDownChannels = (): Array<[string, ChannelHealth]> =>
  Array.from(realtimeChannelHealth.entries()).filter(([, health]) => health.downSince !== null);

/**
 * Channel names → the distinct TABLES behind them, sorted and de-duplicated.
 *
 * `subscriptionFactory` builds every name as `${tableName}:${supabaseOrgId}`, and
 * `channelInstanceTopic` appends `:${sequence}`, so splitting on the first `:` drops the
 * organization UUID and the sequence and keeps the only part that says WHAT is broken. A name
 * with no `:` is kept whole — an unknown shape is better reported than silently discarded.
 */
export const channelTables = (channelNames: string[]): string[] =>
  Array.from(
    new Set(
      channelNames.map((name) => {
        const [table, ...rest] = name.split(':');
        // A name with no `:` is an unknown shape — report it whole rather than discard it. But a
        // name WITH a `:` and an empty head must never fall back to the raw name: that is exactly
        // the case that would emit `:<organizationUuid>` intact, i.e. the degenerate branch of a
        // redactor emitting its unredacted input. Fail closed.
        if (rest.length === 0) return name;
        return table || '(unnamed)';
      }),
    ),
  ).sort();

/**
 * The server's own error text, with anything that looks like an identifier removed.
 *
 * `health.lastError` is realtime-js's message, built from the server's error payload — and for the
 * failure this reporter exists to catch (a rejected `postgres_changes` binding, the ATL-830
 * grant-drift shape) that payload embeds the channel's connect parameters, including
 * `filter: organization_id=eq.<uuid>`. Shipping it raw would put back the identifier
 * `channelTables` was written to strip, at the exact moment the reporter fires most, and would make
 * an unbounded free-text string a PostHog property.
 *
 * The reason is worth keeping — it is what distinguished a grant drift from a transport failure —
 * so it is redacted rather than dropped, and truncated so one pathological message cannot become
 * the event.
 */
const UUID_ANYWHERE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const FIRESTORE_ID_ANYWHERE = /\b[A-Za-z0-9]{20}\b/g;
export const redactServerReason = (reason: string): string =>
  reason
    .replace(UUID_ANYWHERE, '<uuid>')
    .replace(FIRESTORE_ID_ANYWHERE, '<id>')
    .slice(0, 200);

/**
 * Capture one event describing the whole outage, then escalate or stop.
 */
const reportRealtimeOutage = (): void => {
  outageTimer = null;

  const down = getDownChannels();
  if (down.length === 0) {
    // Everything recovered between the timer being armed and it firing.
    outageReportIndex = 0;
    reportedDownChannels = new Set();
    return;
  }

  const client = getSupabaseClient();
  const socketConnected = client ? client.realtime.isConnected() : false;
  const neverSubscribed = down.filter(([, h]) => !h.everSubscribed).map(([name]) => name);
  const dropped = down.filter(([, h]) => h.everSubscribed).map(([name]) => name);
  const outageStartedAt = Math.min(...down.map(([, h]) => h.downSince as number));
  const serverReasons = Array.from(
    new Set(down.map(([, h]) => h.lastError).filter((reason): reason is string => reason !== null))
  );

  const isStructural = socketConnected;
  const message = isStructural
    ? `Supabase realtime: ${down.length} channel(s) rejected while the socket is connected`
    : `Supabase realtime: ${down.length} channel(s) down while the socket is disconnected`;

  // ATL-1354 — MEASUREMENT, NOT AN EXCEPTION.
  //
  // This was `Sentry.captureException(new Error(message))`. Nothing threw: the Error existed only
  // to carry the numbers, so its stack pointed at this reporter and never at a cause. It was the
  // single largest consumer of a 5,000/month error allowance (117 events), and when that allowance
  // ran out on 2026-09-09 the channel stayed dark for nine days — including for real crashes.
  //
  // The breadcrumb stays because breadcrumbs are FREE and ride along with whatever throws next,
  // which is precisely when knowing the realtime channels were down is worth having.
  Sentry.addBreadcrumb({
    category: 'supabase.realtime',
    level: isStructural ? 'error' : 'warning',
    message,
    data: { channelsDown: down.length, socketConnected, reportIndex: outageReportIndex },
  });
  captureOperationalEvent('supabase_realtime_outage', {
    // Kept under the old names so anything already graphing these keeps working.
    supabase_realtime_socket_connected: socketConnected,
    supabase_realtime_failure_kind: isStructural ? 'channel_rejected' : 'transport',
    channelsDown: down.length,
    channelsTimedOut: down.filter(([, h]) => h.lastFailureTimedOut).length,
    channelsTracked: realtimeChannelHealth.size,
    // TABLES, not channel names: a channel is `${table}:${organizationUuid}`, and the UUID is both
    // redundant (PostHog already groups by organization) and a cardinality explosion.
    neverSubscribedTables: channelTables(neverSubscribed),
    droppedTables: channelTables(dropped),
    outageDurationMs: Date.now() - outageStartedAt,
    reportIndex: outageReportIndex,
    serverReasons: serverReasons.map(redactServerReason),
  });

  for (const [name] of down) reportedDownChannels.add(name);
  outageReportIndex += 1;
  reconcileOutageTimer();
};

/**
 * Arm, re-arm or disarm the outage timer to match the current health map.
 */
const reconcileOutageTimer = (): void => {
  const down = getDownChannels();

  if (down.length === 0) {
    if (outageTimer !== null) {
      clearTimeout(outageTimer);
      outageTimer = null;
    }
    outageReportIndex = 0;
    reportedDownChannels = new Set();
    return;
  }

  // A channel nobody has reported yet is new information, so it re-arms an exhausted ladder.
  // While the ladder still has steps left this is a no-op — an ongoing outage escalates normally
  // and picks up newly-failed channels in its next scheduled report rather than an extra one.
  if (
    outageReportIndex >= REALTIME_OUTAGE_REPORT_DELAYS_MS.length &&
    down.some(([name]) => !reportedDownChannels.has(name))
  ) {
    outageReportIndex = 0;
  }

  if (outageTimer === null && outageReportIndex < REALTIME_OUTAGE_REPORT_DELAYS_MS.length) {
    outageTimer = setTimeout(reportRealtimeOutage, REALTIME_OUTAGE_REPORT_DELAYS_MS[outageReportIndex]);
  }
};

/**
 * Stop tracking a channel that is no longer subscribed on purpose.
 *
 * Without this a torn-down channel stays "down" forever and manufactures a
 * permanent outage. `unsubscribe()` only emits CLOSED on its `ok`/`timeout`
 * paths — its `error` path resolves without one — so `unsubscribeChannel()`
 * clears the entry directly as well.
 */
const forgetChannel = (channelName: string): void => {
  if (realtimeChannelHealth.delete(channelName)) {
    reconcileOutageTimer();
  }
};

/** Whether a realtime channel is currently joined. */
export type RealtimeChannelState = 'subscribed' | 'down';

/**
 * Notified on each transition of one channel between joined and not-joined.
 *
 * `gap` is true when events may have been missed since the consumer was last
 * in sync — any SUBSCRIBED that follows a failure or an earlier subscription.
 *
 * ATL-1165: this exists because `postgres_changes` is an event stream, not a
 * state sync. It replays nothing on rejoin and delivers nothing while the
 * channel is down, so a consumer that CACHES the stream (rather than merely
 * reacting to it) has no way to know its cache went stale. Firestore's
 * `onSnapshot` re-delivers current state on reconnect and needs no such
 * signal; every Supabase consumer that caches does.
 */
export type RealtimeStateListener = (state: RealtimeChannelState, gap: boolean) => void;

export interface CreateRealtimeChannelOptions {
  /**
   * Called on each joined/not-joined transition. Fires once per transition,
   * NOT once per status callback: one dead socket fans `CHANNEL_ERROR` out
   * across every open channel (see the ATL-961 note above), and a listener
   * driving a refetch must not be called ~25 times for one event.
   */
  onStateChange?: RealtimeStateListener;
}

/**
 * Single definition of the subscription-status reporting policy, shared by
 * every channel factory below so the two cannot drift apart.
 */
const handleSubscribeStatus = (
  channelName: string,
  status: `${REALTIME_SUBSCRIBE_STATES}`,
  error?: Error,
  onStateChange?: RealtimeStateListener
): void => {
  switch (status) {
    case REALTIME_SUBSCRIBE_STATES.SUBSCRIBED: {
      const health = getChannelHealth(channelName);
      if (health.downSince === null) {
        console.log(`✅ Subscribed to ${channelName}`);
      } else {
        // Recovery leaves a breadcrumb, not an event: without it a human
        // reading Sentry cannot tell a resolved outage from an ongoing one.
        Sentry.addBreadcrumb({
          category: 'supabase.realtime',
          level: 'info',
          message: `Realtime channel recovered: ${channelName}`,
          data: { channel: channelName, downForMs: Date.now() - health.downSince },
        });
      }
      // Read BEFORE the two fields below are reset: a rejoin that follows a
      // failure, or any subscribe after the channel's first, is a window in
      // which the stream delivered nothing and replays nothing.
      const gap = health.downSince !== null || health.everSubscribed;
      health.everSubscribed = true;
      health.downSince = null;
      health.lastError = null;
      reconcileOutageTimer();
      onStateChange?.('subscribed', gap);
      break;
    }
    case REALTIME_SUBSCRIBE_STATES.CHANNEL_ERROR:
    case REALTIME_SUBSCRIBE_STATES.TIMED_OUT: {
      const health = getChannelHealth(channelName);
      if (health.downSince === null) {
        health.downSince = Date.now();
        // Inside the transition guard, so one socket-wide fan-out notifies the
        // consumer once per channel rather than once per retry.
        onStateChange?.('down', false);
        // Log only the transition, not every re-fire. Console lines become
        // Sentry breadcrumbs, and 25 of them per failed reconnect attempt
        // would evict the app.lifecycle / network-failure breadcrumbs that
        // are the only way to tell WHY the socket died.
        console.error(`❌ Realtime channel ${status}: ${channelName}`);
      }
      health.lastFailureTimedOut = status === REALTIME_SUBSCRIBE_STATES.TIMED_OUT;
      if (error?.message) {
        health.lastError = error.message;
      }
      reconcileOutageTimer();
      break;
    }
    case REALTIME_SUBSCRIBE_STATES.CLOSED: {
      // CLOSED is a not-joined transition like any other, and a caching
      // consumer has to hear about it. realtime-js emits it from `unsubscribe()`
      // (our own teardown, where the listener is already stopped and this is
      // inert) AND from a server-sent `phx_close` — a channel terminated by the
      // server, which Phoenix does NOT auto-rejoin. Without this the ATL-1165
      // safety poll would stay disarmed against a permanently dead channel,
      // which is the exact frozen-Today-card failure this signal exists to
      // prevent. Reported before `forgetChannel`, which drops the health entry.
      onStateChange?.('down', false);
      forgetChannel(channelName);
      break;
    }
    default:
      break;
  }
};

// ---------------------------------------------------------------------------
// One realtime-js channel object per call
// ---------------------------------------------------------------------------
//
// ATL-1405 — "mismatch between server and client bindings for postgres changes".
//
// `client.channel(topic)` does NOT always create a channel: when a channel with that topic is still
// registered on the socket it RETURNS THE EXISTING ONE, and `.on('postgres_changes', …)` then
// appends a second binding to it. `.subscribe()` on that already-joined channel is a no-op, so
// nothing fails yet — the join payload the server holds still lists ONE binding. The next rejoin
// (any socket drop) gets ONE binding back while the client holds TWO, and realtime-js's own check
// in `RealtimeChannel.subscribe` fails the channel with exactly that message, unsubscribes it and
// never rejoins it. Measured 2026-09-16..23: every such outage reported exactly 23 channels — the
// `setupAllSubscriptions` set, which AppStack rebuilt on each `[db, user]` change without tearing
// the previous set down — and none recovered (one ran 2h01m, 10 reports, until the app restarted).
//
// The AppStack leak that reached it is fixed at its source, but any caller that opens a name it has
// not closed would reproduce the same permanent outage, so the invariant is enforced here: a
// per-instance topic makes every create a genuinely new channel. The topic is a client-side label
// for public `postgres_changes` channels; the server filters on the binding, not on the name.
// (`unsubscribeChannel` then an immediate re-create is NOT a second path in realtime-js 2.99:
// `unsubscribe()` marks the channel leaving before its `_canPush()` check, so it deregisters
// synchronously. Pinned by realtimeChannelReuse.atl1405.test.js in case a version changes that.)
let channelInstanceSeq = 0;

/** The realtime topic for one channel instance: the logical name plus a per-session sequence. */
export const channelInstanceTopic = (channelName: string): string => {
  channelInstanceSeq += 1;
  return `${channelName}:${channelInstanceSeq}`;
};

/**
 * Create a real-time channel for a specific table
 * @param channelName - Logical channel name (`${table}:${organizationUuid}`); each call opens a
 *   NEW channel instance under a unique topic derived from it — see `channelInstanceTopic`
 * @param tableName - Table name to subscribe to
 * @param organizationId - Organization ID filter
 * @param callback - Callback function for changes
 * @returns Supabase real-time channel
 */
export const createRealtimeChannel = <T extends TableName>(
  channelName: string,
  tableName: T,
  organizationId: string,
  callback: (payload: RealtimePayload<T>) => void,
  options?: CreateRealtimeChannelOptions
): RealtimeChannel | null => {
  const client = getSupabaseClient();
  if (!client) {
    console.warn('Supabase client not available, cannot create real-time channel');
    return null;
  }

  const topic = channelInstanceTopic(channelName);
  const channel = client
    .channel(topic)
    .on(
      'postgres_changes',
      {
        event: '*', // INSERT, UPDATE, DELETE
        schema: 'public',
        table: tableName,
        filter: `organization_id=eq.${organizationId}`,
      },
      (payload) => {
        callback(payload as unknown as RealtimePayload<T>);
      }
    )
    .subscribe((status, error) => {
      handleSubscribeStatus(topic, status, error, options?.onStateChange);
    });

  return channel;
};

/**
 * Create a real-time channel for the organizations table
 * Uses firestore_id filter instead of organization_id since organizations table doesn't have organization_id
 * @param channelName - Logical channel name; a new instance per call, as for `createRealtimeChannel`
 * @param firestoreOrgId - Firestore organization ID to filter by
 * @param callback - Callback function for changes
 * @param options - Same state listener as `createRealtimeChannel`
 * @returns Supabase real-time channel
 */
export const createOrganizationRealtimeChannel = (
  channelName: string,
  firestoreOrgId: string,
  callback: (payload: RealtimePayload<'organizations'>) => void,
  options?: CreateRealtimeChannelOptions
): RealtimeChannel | null => {
  const client = getSupabaseClient();
  if (!client) {
    console.warn('Supabase client not available, cannot create real-time channel');
    return null;
  }

  const topic = channelInstanceTopic(channelName);
  const channel = client
    .channel(topic)
    .on(
      'postgres_changes',
      {
        event: '*', // INSERT, UPDATE, DELETE
        schema: 'public',
        table: 'organizations',
        filter: `firestore_id=eq.${firestoreOrgId}`,
      },
      (payload) => {
        callback(payload as unknown as RealtimePayload<'organizations'>);
      }
    )
    .subscribe((status, error) => {
      handleSubscribeStatus(topic, status, error, options?.onStateChange);
    });

  return channel;
};

/**
 * Unsubscribe from a real-time channel
 * @param channel - Channel to unsubscribe from
 */
export const unsubscribeChannel = (channel: RealtimeChannel | null): void => {
  if (channel) {
    // Drop health tracking first: `unsubscribe()`'s error path never emits
    // CLOSED, and a channel left marked "down" after a deliberate teardown
    // would keep the outage timer armed and report a failure that isn't one.
    forgetChannel(channel.topic.replace(/^realtime:/, ''));
    const client = getSupabaseClient();
    if (client) {
      void client.removeChannel(channel);
    }
  }
};

// Re-export common types from shared package for convenience
export type {
  Product,
  ProductInsert,
  ProductUpdate,
  Organization,
  Store,
  User,
  Transaction,
  Client,
  Row,
  Insert,
  Update,
} from '@atlas/supabase-types';

export default getSupabaseClient;
